import { PlaneGeometry, ShaderMaterial, Mesh, DoubleSide, Vector2, Color, Vector3 } from 'three';
import vertexShader from '../shaders/sky/vertex.glsl';
import fragmentShader from '../shaders/sky/fragment.glsl';
import BackgroundScene from './BackgroundScene.js';
import Camera from './Camera.js';
import Sizes from '../utils/Sizes.js';

export default class Sky {
    constructor() {
        this.backgroundScene = BackgroundScene.getInstance();
        this.scene = this.backgroundScene.getScene();
        this.grid = this.backgroundScene.grid;
        this.sizes = Sizes.getInstance();
        const gridDimensions = this.grid.getGridSize();
        this.gridSize = gridDimensions.width;
        this.horizonPosition = this.grid.getHorizonPosition();

        // Store camera reference
        this.camera = Camera.getInstance().cameraBackground;

        this.calculateSkyHeight();
        this.init();

        // Add resize event listener
        this.sizes.on('resize', this.resize.bind(this));
    }

    calculateSkyHeight() {
        const fov = this.camera.fov * (Math.PI / 180);
        const cameraPos = this.camera.position.clone();
        const distanceToHorizon = Math.abs(cameraPos.z - this.horizonPosition);
        const theoreticalHeight = 2 * distanceToHorizon * Math.tan(fov/2);
        this.skyHeight = theoreticalHeight * 0.82;
    }

    init() {
        const skyGeometry = new PlaneGeometry(this.gridSize, this.skyHeight, 1, 1);

        const skyMaterial = new ShaderMaterial({
            vertexShader,
            fragmentShader,
            transparent: true,
            side: DoubleSide,
            uniforms: {
                uTime: { value: 0.0 },
                uTimeScale: { value: 0.3 },
                uGlowIntensity: { value: 0.5 },
                uGlowWidth: { value: 1.0 },
                uHorizontalFalloff: { value: 2.0 },
                uMinLineWidth: { value: 0.2 },
                uResolution: { value: new Vector2(this.sizes.width, this.sizes.height) },
                uColor: { value: new Vector3(0.62, 0.0, 0.59) }, // c902c6
                uBackground: { value: new Color(0x864dbb) }, // 864dbb
                uStarColor: { value: new Color(0xffffff) }, // ffffff
                uStarDensity: { value: 0.01 },
                uStarIntensity: { value: 0.8 },
                uSmallStarDensity: { value: 0.07 },
                uSmallStarIntensity: { value: 0.5 },
                uLineThickness: { value: 0.05 }, // px
                uLineFrequency: { value: 9 },
                uIntensity: { value: 1.0 },
                uIntensityScale: { value: 1.2 },
                uIntensityShift: { value: 1.0 },
                uIntensityTimeScale: { value: 1.3 },
                uBackgroundGradientCenter: { value: 1.0 },
                uBackgroundGradientEdge: { value: -0.76 },
                uBackgroundGradientX: { value: 0.25 },
                uBackgroundGradientY: { value: 1.4 },
                uBackgroundGradientColor1: { value: new Color(0x9850ce) },
                uBackgroundGradientColor2: { value: new Color(0x000308) }
            }
        });

        this.skyMesh = new Mesh(skyGeometry, skyMaterial);

        this.horizonPosition = this.grid.getHorizonPosition();

        this.skyMesh.position.set(0, this.skyHeight / 2, this.horizonPosition - 0.001);
        this.scene.add(this.skyMesh);
    }

    update(delta) {
        if (this.skyMesh) {
            this.skyMesh.material.uniforms.uTime.value += delta;

            // Update resolution on each frame to ensure it's current
            this.skyMesh.material.uniforms.uResolution.value.set(
                this.sizes.width,
                this.sizes.height
            );
        }
    }

    resize() {
        if (this.skyMesh) {
            // Update grid dimensions and horizon position
            const gridDimensions = this.grid.getGridSize();
            this.gridSize = gridDimensions.width;
            this.horizonPosition = this.grid.getHorizonPosition();

            // Recalculate sky height
            this.calculateSkyHeight();

            // Dispose of old geometry and create new one
            this.skyMesh.geometry.dispose();
            this.skyMesh.geometry = new PlaneGeometry(this.gridSize, this.skyHeight, 1, 1);

            // Update sky position
            this.skyMesh.position.set(0, this.skyHeight / 2, this.horizonPosition - 0.001);

            // Update the resolution uniform
            this.skyMesh.material.uniforms.uResolution.value.set(
                this.sizes.width,
                this.sizes.height
            );
        }
    }
}
